import {Injectable} from '@angular/core';
import {Observable, of, Subject} from "rxjs";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {SnackBarService} from "../../../../../@core/services/snack-bar.service";
import {catchError, switchMap} from "rxjs/operators";
import {environment} from "../../../../../../environments/environment";
import {UserService} from "../../../../../@shared/services/user.service";
import {IBid, IMarketItems, ISteamInventory} from "./market.interface";

@Injectable()
export class MarketService {

  private readonly steamItemsRequest: Subject<void> = new Subject<void>();
  private readonly steamItems: Subject<ISteamInventory> = new Subject<ISteamInventory>();
  public readonly steamItems$: Observable<ISteamInventory> = this.steamItems.asObservable();

  private readonly marketItemsRequest: Subject<{ pageSize: number, currentPage: number }> = new Subject<{ pageSize: number, currentPage: number }>();
  private readonly marketItems: Subject<IMarketItems> = new Subject<IMarketItems>();
  public readonly marketItems$: Observable<IMarketItems> = this.marketItems.asObservable();

  private readonly bidsRequest: Subject<void> = new Subject<void>();
  private readonly bids: Subject<IBid> = new Subject<IBid>();
  public readonly bids$: Observable<IBid> = this.bids.asObservable();

  constructor(private readonly http: HttpClient,
              private readonly userService: UserService,
              private sb: SnackBarService) {
    this.subscribeEmitters();
  }

  public requestSteamItems(): void {
    this.steamItemsRequest.next();
  }

  public requestMarketItems(pageSize: number, currentPage: number): void {
    this.marketItemsRequest.next({ pageSize, currentPage });
  }

  public requestBids(): void {
    this.bidsRequest.next();
  }

  private subscribeEmitters(): void {
    this.steamItemsRequest.pipe(
      switchMap(() => this.http.get(`${environment.GATEWAY_URL}/market/steam/inventory/${this.userService.currentUserId.getValue()}`,
        {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))),
    ).subscribe((steamItems: ISteamInventory) => this.steamItems.next(steamItems));

    this.marketItemsRequest.pipe(
      switchMap(({ pageSize, currentPage }) => this.http.get(
        `${environment.GATEWAY_URL}/market/items/${this.userService.currentUserId.getValue()}` +
        `?page[size]=${pageSize}&page[number]=${currentPage + 1}`,
        {withCredentials: true})
        .pipe(catchError((error: HttpErrorResponse) => this.onHandleError(error)))),
    ).subscribe((marketItems: IMarketItems) => this.marketItems.next(marketItems));

    this.bidsRequest.pipe(
      switchMap(() =>
        this.http.get<IBid>(`${environment.GATEWAY_URL}/market/bids/${this.userService.currentUserId.getValue()}`,
          {withCredentials: true})
      ))
      .subscribe((bids: IBid) => {
        this.bids.next(bids)
      });
  }

  private onHandleError(error: HttpErrorResponse): Observable<any> {
    const message = error.error.statusCode + ' ' + (error.error.message || error.error.error);
    this.sb.openSnackBar(message, 3000, 'error');
    return of(null);
  }
}

